import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { propertyData } from '../mockData';
import { propertyHighlights } from '../mockData_extended';
import { Home, Map, Eye, Trees, Mountain, TrendingUp, CheckCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const iconMap = {
  0: Home,
  1: Map,
  2: Eye,
  3: Trees,
  Home: Home,
  Map: Map,
  Eye: Eye,
  Mountain: Mountain,
  Trees: Trees,
  TrendingUp: TrendingUp
};

const Overview = () => {
  const { language } = useLanguage();
  const content = (propertyData[language] || propertyData.en).overview;
  const highlights = propertyHighlights[language] || propertyHighlights.en;
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section id="overview" className="py-16 sm:py-20 bg-white relative overflow-hidden" aria-labelledby="overview-heading">
      {/* Parallax Background Elements */}
      <div 
        className="absolute top-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-gradient-to-br from-[#a8cef1]/20 to-transparent rounded-full blur-3xl"
        style={{ transform: `translateY(${scrollY * 0.1}px)` }}
      ></div>
      <div 
        className="absolute bottom-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-gradient-to-tr from-[#d4845f]/10 to-transparent rounded-full blur-3xl"
        style={{ transform: `translateY(${scrollY * -0.1}px)` }}
      ></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Title and Description */}
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-slate-800 mb-4 sm:mb-6 text-center" id="overview-heading">
          {content.title}
        </h2>
        <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-4xl mx-auto text-center mb-8 sm:mb-10">
          {content.description}
        </p>

        {/* Highlights - Compact Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 max-w-5xl mx-auto mb-10 sm:mb-14">
          {highlights.items.map((item, index) => {
            const Icon = iconMap[item.icon] || CheckCircle;
            return (
              <div
                key={index}
                className="flex items-center gap-3 p-3 sm:p-4 bg-gradient-to-r from-slate-50 to-white rounded-lg border border-slate-100 hover:border-[#d4845f]/30 hover:shadow-md transition-all duration-300"
              >
                <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-[#d4845f] to-[#c17552] rounded-lg flex items-center justify-center">
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <p className="text-sm sm:text-base text-slate-700 leading-snug flex-1">
                  {item.text}
                </p>
              </div>
            );
          })}
        </div>

        {/* Feature Cards - 4 columns on desktop, 2 on tablet, 1 on mobile */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {content.features.map((feature, index) => {
            const Icon = iconMap[index];
            return (
              <Card3D key={index} feature={feature} Icon={Icon} index={index} />
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Card3D = ({ feature, Icon, index }) => {
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / 15;
    const rotateY = (centerX - x) / 15;

    setRotation({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => {
    setRotation({ x: 0, y: 0 });
    setIsHovered(false);
  };

  return (
    <div
      className="perspective-1000"
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
    >
      <Card
        className="border-none shadow-lg transition-all duration-500 bg-gradient-to-br from-white to-slate-50 cursor-pointer h-full"
        style={{
          transform: `
            perspective(1000px) 
            rotateX(${rotation.x}deg) 
            rotateY(${rotation.y}deg) 
            scale(${isHovered ? 1.03 : 1})
          `,
          boxShadow: isHovered 
            ? '0 20px 40px rgba(0,0,0,0.15)' 
            : '0 8px 20px rgba(0,0,0,0.08)'
        }}
      >
        <CardContent className="p-4 sm:p-5 text-center">
          <div 
            className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#a8cef1] to-[#7eb3e3] rounded-full flex items-center justify-center mx-auto mb-3 transition-transform duration-500"
            style={{
              boxShadow: isHovered ? '0 15px 30px rgba(126, 179, 227, 0.3)' : 'none'
            }}
          >
            <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-2">
            {feature.title}
          </h3>
          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
            {feature.description}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Overview;
